/**
 * ColorsTab — background, text, border color controls + text shadow presets.
 * Single responsibility: color-related style properties.
 */

import React from 'react';
import type { StyleProperties } from '../types';
import { ColorPicker } from '../shared/ColorPicker';
import { SectionLabel } from '../shared/SectionLabel';
import { PresetChips } from '../shared/PresetChips';

export interface ColorsTabProps {
  value: StyleProperties;
  onChange: (p: StyleProperties) => void;
  isDark: boolean;
}

const TEXT_SHADOW_PRESETS = [
  { label: 'None', value: 'none' },
  { label: 'Soft', value: '1px 1px 2px rgba(0,0,0,0.2)' },
  { label: 'Medium', value: '2px 2px 4px rgba(0,0,0,0.3)' },
  { label: 'Strong', value: '0 2px 8px rgba(0,0,0,0.5)' },
];

/**
 * ColorsTab — color pickers for background, text, and border colors plus text shadow.
 */
export const ColorsTab: React.FC<ColorsTabProps> = ({ value, onChange, isDark }) => {
  const textPrimary = isDark ? '#e2e8f0' : '#1e293b';
  const inputBg = isDark ? 'rgba(2,6,23,0.6)' : '#f8fafc';
  const borderColor = isDark ? 'rgba(51,65,85,0.5)' : '#e2e8f0';

  const sectionStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  };

  const shadowInputStyle: React.CSSProperties = {
    width: '100%',
    padding: '5px 8px',
    borderRadius: 4,
    border: `1px solid ${borderColor}`,
    background: inputBg,
    color: textPrimary,
    fontSize: 12,
    boxSizing: 'border-box',
    fontFamily: 'monospace',
  };

  return (
    <div style={sectionStyle}>
      <ColorPicker
        label="Background"
        value={value.backgroundColor}
        onChange={(v) => onChange({ ...value, backgroundColor: v })}
        isDark={isDark}
      />
      <ColorPicker
        label="Text Color"
        value={value.color}
        onChange={(v) => onChange({ ...value, color: v })}
        isDark={isDark}
      />
      <ColorPicker
        label="Border Color"
        value={value.borderColor}
        onChange={(v) => onChange({ ...value, borderColor: v })}
        isDark={isDark}
      />

      <SectionLabel label="Text Shadow" isDark={isDark} />

      <PresetChips
        chips={TEXT_SHADOW_PRESETS}
        value={value.textShadow}
        onChange={(v) => onChange({ ...value, textShadow: v === 'none' ? undefined : v })}
        isDark={isDark}
      />

      <input
        type="text"
        value={value.textShadow ?? ''}
        onChange={(e) => onChange({ ...value, textShadow: e.target.value || undefined })}
        placeholder="e.g. 1px 1px 2px rgba(0,0,0,0.2)"
        style={shadowInputStyle}
        aria-label="Text shadow custom value"
        spellCheck={false}
      />
    </div>
  );
};

ColorsTab.displayName = 'ColorsTab';
